import { createReducer, isAnyOf } from '@reduxjs/toolkit';
import type { ICommonObjectRead, IDocument, IMediaTypeRead, ISecureDocumentStoreSettings, ITag, IVault } from '../../api/domain';
import {
    fetchAllDocumentsSuccess,
    fetchDocumentsByIdsSuccess,
    fetchDocumentSuccess,
    fetchMediaTypesSuccess,
    fetchSettingsSuccess,
    fetchTagsSuccess,
    fetchTotalAmountOfDocumentsSuccess,
    fetchUnreadDocumentCountSuccess,
    fetchVaultsSuccess,
} from './secure-document-store-api-actions';
import { getZipTypeVariants } from '../../utils/mime-util';

interface ISecureDocumentStoreApiState {
    currentDocument?: IDocument;
    documents: IDocument[];
    vaults: IVault[];
    tags: ITag[];
    mediaTypes: IMediaTypeRead[];
    settings: ISecureDocumentStoreSettings;
    unreadDocumentsCount: number;
    totalDocumentsCount: number;
}
export type { ISecureDocumentStoreApiState };

export const initialState: ISecureDocumentStoreApiState = {
    documents: [],
    vaults: [],
    tags: [],
    mediaTypes: [],
    unreadDocumentsCount: 0,
    settings: {
        serviceCenterEmailAddress: '',
        moduleEnabled: false,
        maxDocumentVersionsPerVault: 1,
        maxUploadFileSize: 52_428_800,
    },
    totalDocumentsCount: 0,
};

export const secureDocumentStoreApiReducer = createReducer(initialState, (builder) => {
    builder
        .addCase(fetchUnreadDocumentCountSuccess, (state, { payload: unreadDocumentsCount }) => {
            state.unreadDocumentsCount = unreadDocumentsCount;
        })
        .addCase(fetchVaultsSuccess, (state, { payload: vaults }) => {
            state.vaults = vaults;
        })
        .addCase(fetchMediaTypesSuccess, (state, { payload: mediaTypes }) => {
            // TODO: OBB-874: Once the product team implements the other media types, remove this whole conditional part
            if (mediaTypes.some((mediaType) => mediaType.type === 'application/zip')) {
                mediaTypes.push(
                    ...Object.keys(getZipTypeVariants()).map((type) => ({
                        type,
                        extension: 'zip',
                        commonObject: {} as ICommonObjectRead,
                    })),
                );
            }
            state.mediaTypes = mediaTypes;
        })
        .addCase(fetchTagsSuccess, (state, { payload: tags }) => {
            state.tags = tags;
        })
        .addCase(fetchSettingsSuccess, (state, { payload: settings }) => {
            // OBB-708: Temporarily override the maxUploadFileSize - we might want to show this option to big-admin in workbench
            state.settings = { ...settings, maxUploadFileSize: initialState.settings.maxUploadFileSize };
        })
        .addCase(fetchTotalAmountOfDocumentsSuccess, (state, { payload: totalDocumentsCount }) => {
            state.totalDocumentsCount = totalDocumentsCount;
        })
        .addCase(fetchDocumentSuccess, (state, { payload: currentDocument }) => {
            state.currentDocument = currentDocument;
        })
        .addMatcher(isAnyOf(fetchAllDocumentsSuccess, fetchDocumentsByIdsSuccess), (state, { payload: documents }) => {
            state.documents = documents;
        });
});

export default secureDocumentStoreApiReducer;
